import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import PasswordStrengthIndicator from '../../components/shared/PasswordStrengthIndicator';
import { validateRegistrationPassword } from '../../utils/passwordValidation';
import { signInWithGoogle, isOAuthConfigured } from '../../services/oauthService';
import AuthPage from './AuthPage';

const RoleChoice = ({ value, onChange, disabled }) => (
  <div>
    <p className="mb-2 text-sm font-medium text-m-ink">I am a</p>
    <div className="grid grid-cols-2 gap-3">
      {[
        { id: 'client', title: 'Client', hint: 'Looking for vendors' },
        { id: 'vendor', title: 'Vendor', hint: 'Offering services' },
      ].map((role) => {
        const selected = value === role.id;
        return (
          <button
            key={role.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(role.id)}
            className={`rounded-2xl border px-3 py-3.5 text-center transition-colors disabled:opacity-50 ${
              selected
                ? 'border-m-ink bg-m-surface'
                : 'border-m-border-light bg-m-surface hover:border-m-ink/30'
            }`}
          >
            <span className="block text-base font-medium text-m-ink">{role.title}</span>
            <span className="mt-0.5 block text-sm text-m-ink-muted">{role.hint}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const GoogleMark = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const AppleMark = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M16.37 12.72c.03 3.18 2.79 4.24 2.82 4.25-.02.07-.44 1.51-1.45 2.99-.87 1.28-1.78 2.55-3.2 2.58-1.4.03-1.85-.83-3.45-.83s-2.11.8-3.43.86c-1.38.05-2.43-1.38-3.31-2.65-1.8-2.6-3.18-7.35-1.33-10.55.92-1.59 2.56-2.6 4.34-2.62 1.35-.03 2.63.91 3.45.91.82 0 2.35-1.12 3.96-.96.67.03 2.57.27 3.79 2.05-.1.06-2.26 1.32-2.19 3.97zM14.3 6.3c.72-.87 1.2-2.08 1.07-3.3-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-.09 3.1 1.15.09 2.32-.58 2.04-1.36z" />
  </svg>
);

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    user_type: 'client',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  const { register, oauthLogin } = useAuth();
  const navigate = useNavigate();
  const oauthConfig = isOAuthConfigured();

  const goAfterAuth = (userType) => {
    if (userType === 'vendor') navigate('/vendor');
    else if (userType === 'client') navigate('/dashboard');
    else navigate('/admin');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    setErrors({});

    try {
      const passwordValidation = validateRegistrationPassword(formData.password, formData.password);
      if (!passwordValidation.isValid) {
        setErrors(passwordValidation.errors);
        setError('Please fix the password errors below');
        setLoading(false);
        return;
      }

      const result = await register({
        ...formData,
        confirmPassword: formData.password,
      });

      if (result.success) {
        goAfterAuth(formData.user_type);
      } else if (result.error && typeof result.error === 'object') {
        setErrors(result.error);
        setError('Please fix the errors below');
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (oauthLoading || loading) return;
    setOauthLoading(true);
    setError('');
    setErrors({});
    try {
      const oauthData = await signInWithGoogle();
      const result = await oauthLogin(oauthData, formData.user_type, 'register');
      if (result.success) {
        goAfterAuth(formData.user_type);
      } else if (result.error && typeof result.error === 'object') {
        setErrors(result.error);
        const firstFieldError = Object.values(result.error)
          .flat()
          .find((value) => typeof value === 'string' && value.trim());
        setError(firstFieldError || 'Please fix the errors below');
      } else {
        setError(
          result.error ||
            'An account with this email already exists. Please sign in instead.'
        );
      }
    } catch (err) {
      setError(err.message || 'Google sign in failed. Please try again.');
    } finally {
      setOauthLoading(false);
    }
  };

  const fieldError = (key) => {
    const value = errors[key];
    if (!value) return null;
    return Array.isArray(value) ? value[0] : value;
  };

  return (
    <AuthPage>
      <h1 className="marketing-display-md text-center text-m-ink">Create an account</h1>
      <p className="marketing-body mt-2 text-center text-m-ink-muted">
        Join as a client or vendor
      </p>

      <div className="mt-8">
        <RoleChoice
          value={formData.user_type}
          onChange={(user_type) => setFormData((prev) => ({ ...prev, user_type }))}
          disabled={loading || oauthLoading}
        />

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setError('Apple sign-in is coming soon.')}
            className="auth-btn-outline"
          >
            <AppleMark />
            Apple
          </button>
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={oauthLoading || loading || !oauthConfig.google}
            className="auth-btn-outline"
          >
            {oauthLoading ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-m-border-light border-t-m-ink" />
            ) : (
              <GoogleMark />
            )}
            Google
          </button>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-m-border-light" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-m-canvas-light px-3 text-m-ink-faint">or</span>
          </div>
        </div>

        <form className="space-y-3" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <input
                name="first_name"
                type="text"
                autoComplete="given-name"
                required
                value={formData.first_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, first_name: e.target.value }))
                }
                placeholder="First name"
                className="auth-input"
              />
              {fieldError('first_name') && (
                <p className="mt-1 text-xs text-red-600">{fieldError('first_name')}</p>
              )}
            </div>
            <div>
              <input
                name="last_name"
                type="text"
                autoComplete="family-name"
                required
                value={formData.last_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, last_name: e.target.value }))
                }
                placeholder="Last name"
                className="auth-input"
              />
              {fieldError('last_name') && (
                <p className="mt-1 text-xs text-red-600">{fieldError('last_name')}</p>
              )}
            </div>
          </div>
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
            placeholder="Email"
            className="auth-input"
          />
          {fieldError('email') && (
            <p className="-mt-1 text-xs text-red-600">{fieldError('email')}</p>
          )}
          <div className="relative">
            <input
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={formData.password}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, password: e.target.value }))
              }
              placeholder="Password"
              className="auth-input pr-11"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-m-ink-faint hover:text-m-ink"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {fieldError('password') && (
            <p className="-mt-1 text-xs text-red-600">{fieldError('password')}</p>
          )}
          <PasswordStrengthIndicator password={formData.password} />
          <p className="text-center text-xs leading-relaxed text-m-ink-muted">
            By signing up, you agree to the{' '}
            <Link to="/terms" className="underline underline-offset-2">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline underline-offset-2">
              Privacy Policy
            </Link>
            .
          </p>
          <button
            type="submit"
            disabled={loading}
            className="marketing-btn marketing-btn-ink w-full"
          >
            {loading ? 'Creating account…' : 'Sign up'}
          </button>
          <p className="text-center text-sm text-m-ink-muted">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-m-ink underline underline-offset-2">
              Log in
            </Link>
          </p>
        </form>
      </div>
    </AuthPage>
  );
};

export default Register;
